import createAxios from 'App/utils/createAxios';
import config from '../components/config/config';
import getSlsBasePath from '../utils/getSlsBasePath/getSlsBasePath';
import * as R from 'ramda';

const slsBasePath = getSlsBasePath(config, 'company');

const company = createAxios({
    baseURL: `${slsBasePath}/company/${config.slsVersion}`,
});

const ignoreNotFound = (error) => {
    const { status } = error.response;
    // Hide toast in my account page if ehrBranchSetting returns a 404 error
    return status !== 404;
};

const companyNoSlug = createAxios({
    baseURL: `${slsBasePath}/${config.slsVersion}`,
});

class CompanyService {
    static company = companyNoSlug;

    static noToastCompany = createAxios(
        {
            baseURL: `${slsBasePath}/${config.slsVersion}`,
        },
        { shouldShowToastFn: ignoreNotFound }
    );

    /**
     * Fetches all custom menus for a company.
     * @returns {Promise<customMenuOptions>} - An array of custom menu options.
     */
    static getCustomMenus() {
        return companyNoSlug.get(`/custom-menu`).then((data) => {
            for (const [menuName, menuOptions] of Object.entries(data)) {
                window.localStorage.setItem(`custom_menu_${menuName}`, JSON.stringify(menuOptions));
            }
            return data;
        });
    }

    /**
     * Fetches a custom menu.
     * @param menuName - The custom menu name.
     * @returns {*[]|*} - An array of custom menu options.
     */
    static getCustomMenu(menuName) {
        let menuOptions = window.localStorage.getItem(`custom_menu_${menuName}`);
        try {
            menuOptions = JSON.parse(menuOptions);
            return menuOptions;
        } catch (_e) {
            return [];
        }
    }

    /**
     * Fetches the available therapeutic categories.
     * @returns {Promise<categories>} - A map of therapeutic categories keyed by ID.
     */
    static getTherapyCategory(therapyId) {
        return companyNoSlug.get(`/therapy/${therapyId}`);
    }

    /**
     * Fetches the available therapeutic categories.
     * @returns {Promise<categories>} - A map of therapeutic categories keyed by ID.
     */
    static getTherapyCategories() {
        return companyNoSlug.get(`/therapy`);
    }

    /**
     * Fetches the available diagnosis codes for the provided therapeutic category.
     * @param {string} therapyId - The ID of the category.
     * @param {number} type - The diagnosis revision number.
     * @returns {Promise<diagnosisCodes>}
     */
    static getTherapyDiagnosis(therapyId, type = 10) {
        return companyNoSlug.get(`/therapy/${therapyId}/diagnosis/${type}`);
    }

    /**
     * Fetches the available medications for the provided therapeutic cateogry
     * @param {string} therapyId - The ID of the category.
     * @returns {Promise<medications>}
     */
    static getTherapyMedications(therapyId) {
        return companyNoSlug.get(`/therapy/${therapyId}/medication`);
    }

    static getCompanyUsers(reqBody = {}) {
        return company.post('/users', {
            ...reqBody,
        });
    }

    static getPendingCompanySsoUsers() {
        return company.get('/users/sso');
    }

    static getCompanyUser(userId, disabledOnly = false) {
        return company.get(`/user/${userId}`, {
            params: {
                disabledOnly,
            },
        });
    }

    static getCompanyUserRoles(userId) {
        return company.get(`/user/${userId}/roles`);
    }

    static getCompanyUserPrivileges(userId) {
        return company.get(`/user/${userId}/privileges`);
    }

    static getLicencedMedications(limit, query, grouped) {
        const params = {};

        if (limit !== undefined) params.limit = limit;
        if (query !== undefined) params.query = query;
        if (grouped !== undefined) params.grouped = grouped;

        if (Object.keys(params).length) {
            return company.get('/medications', { params });
        }

        return company.get('/medications');
    }

    static getServerTime() {
        return company.get('/resource/serverTime');
    }

    static lockForUpdate(lockData) {
        return company.put('/resource/lastSaved', lockData);
    }

    /**
     * @typedef BranchSetting
     * @property {string} uuid
     * @property {string} branchId
     * @property {string} companyId
     * @property {string} keyValue
     * @property {string} keyType
     */

    /**
     * Fetches all branch settings for all branches for the current company
     * @param {string} keyType - The ID of the category.
     * @returns {Promise<BranchSetting[]>}
     */
    static getAllBranchSettingsForType(keyType) {
        return companyNoSlug.get(`/setting/${keyType}/all`);
    }

    static getAllCompanies() {
        return companyNoSlug.get(`/companies`);
    }

    /**
     * Fetches the branch setting for the user. assumes current users branchId is the one they want.
     * @param {string} keyType - The ID of the category.
     * @param {string} defaultValue - default value
     * @param {Boolean} convertToBoolean - converts ['true', '1'] to true, other values will return false
     * @param {string} branchId - the ID of the desired branch to search for setting (optional)
     * @returns {Promise<any>}
     */
    static getBranchSettings(keyType, defaultValue, convertToBoolean, branchId = null, fetchAll = false) {
        return this.noToastCompany.get(`/setting/${keyType}`, {
            params: { defaultValue, convertToBoolean, branchId, fetchAll },
        });
    }

    /**
     * Fetches EHR related branch settings
     * @returns {Promise<object>} A keyed hash of all EHR branch settings for the current branch
     */
    static getEhrBranchSettings() {
        return companyNoSlug.get(`/ehr/settings`);
    }

    /**
     * Updates api medication configuration for enrollment/management
     * @param therapyId
     * @param data
     * @returns {Promise<AxiosResponse<any>>}
     */
    static patchApiMedications = async (therapyId, data) => {
        // eslint-disable-next-line no-param-reassign
        data = R.map(R.reject(R.equals(null)))(data);
        return companyNoSlug.patch(`/managedMedications/${therapyId}`, data);
    };

    /**
     * Fetches all dev features for a company.
     * @returns {Promise<featureFlagOptions>} - An array of dev feature flags options.
     */
    static getDevFeatureFlags = async () => {
        return companyNoSlug.get(`/features`).then(R.compose(R.map(R.prop('isEnabled')), R.indexBy(R.prop('name'))));
    };

    /**
     * Get Required Patient Fields
     * @param params - The filter params.
     * @returns {Promise} - A required patient fields response object
     */
    static getRequiredPatientFields(companyId) {
        return companyNoSlug.get(`/company/${companyId}/requiredPatientFields`);
    }

    /**
     * Gets sso configuration for a particular company.
     * @param therapyId
     * @returns {Promise<AxiosResponse<any>>}
     */
    static getSsoCompanyConfiguration(companyId) {
        return company.get(`/ssoConfiguration/${companyId}`, {
            clearCacheEntry: true,
        });
    }

    /**
     * Adds sso configuration for a particular company.
     * @param therapyId
     * @returns {Promise<AxiosResponse<any>>}
     */
    static addSsoCompanyConfiguration(ssoConfig) {
        return company.post(`/ssoConfiguration`, ssoConfig);
    }

    /**
     * Generates mobile activity links for all new & pending mobile-enabled activities for the
     * company and stores them in the `patient_generated_activity_links` table.
     * Backend must reject the request unless the caller is a Company Admin with the
     * GENERATE_MOBILE_LINK CRM configuration enabled.
     * @returns {Promise<AxiosResponse<any>>}
     */
    static generateActivityMobileLinks(branchId) {
        return company.post(`/branch/${branchId}/mobile-links`);
    }

    /**
     * Downloads a presigned URL of mobile activity links for all new & pending mobile-enabled activities for the
     * company and stores them in the `patient_generated_activity_links` table.
     * Backend must reject the request unless the caller is a Company Admin with the
     * GENERATE_MOBILE_LINK CRM configuration enabled.
     * @returns {Promise<any>}
     */
    static getActivityMobileLinksPresignedUrl(branchId, downloadId) {
        return company.get(`/branch/${branchId}/mobile-links/${downloadId}/presigned-url`);
    }

    /**
     * Get unassigned medication count.
     * @param filter and search parameters
     * @returns {Promise<AxiosResponse<any>>}
     */
    static getUnassignedMedicationCount(params) {
        return companyNoSlug.post(`/company/unassigned-medication-count`, params);
    }

    /**
     * Get unassigned medication list.
     * @param filter and search parameters
     * @returns {Promise<AxiosResponse<any>>}
     */
    static getUnassignedMedication(params) {
        return companyNoSlug.post(`/company/unassigned-medication`, params);
    }

    /**
     * Updates sso configuration for a particular company.
     * @param therapyId
     * @returns {Promise<AxiosResponse<any>>}
     */
    static updateSsoCompanyConfiguration(ssoConfig) {
        return company.put(`/ssoConfiguration/`, ssoConfig);
    }

    static getCompanyBranches(isActive) {
        return company.get(`/branches`, {
            params: { isActive },
        });
    }

    static getCompanyBranch(branchId) {
        return company.get(`/branches/${branchId}`);
    }

    static updateCompanyBranch(branchId, payload) {
        return company.patch(`/branch/${branchId}`, payload);
    }

    static getCompanyRoles() {
        return company.get('/roles');
    }

    static createCompanyUser(user) {
        return company.post('/user', user);
    }

    static updateCompanyUser(userId, user) {
        return company.patch(`/user/${userId}`, user);
    }

    static disableCompanyUser(userId) {
        return company.delete(`/user/${userId}`);
    }

    static enableCompanyUser(userId, branchIds) {
        return company.put(`/user/${userId}`, branchIds);
    }
}

export default CompanyService;
